/**
 * Condition DSL — self-describing typed conditions that evaluate to boolean.
 * Used for visibility, validation guards, step guards, and any boolean logic in schemas.
 *
 * Keys are the operators — no generic `op` discriminator.
 * A non-tech person should be able to read conditions like plain English:
 *   { field: '{ns}.country', notEquals: true }
 *   { allOf: [{ field: 'x', equals: 1 }, { field: 'y', notEmpty: true }] }
 */

// ─── Union ───────────────────────────────────────────────────
export type Condition =
  | FieldCondition
  | AllOfCondition
  | AnyOfCondition
  | NotCondition
  | OptionPropCondition
  | MetaCondition
  | CustomCondition
  | ChangedCondition
  | ValueEqualsCondition
  | ValueNotEqualsCondition
  | ValueIsEmptyCondition
  | ValueNotEmptyCondition
  | ValueGtCondition
  | ValueGteCondition
  | ValueLtCondition
  | ValueLteCondition
  | ValueInCondition
  | ValueMatchesCondition;

// ─── Field conditions (check another field's value) ──────────
export interface FieldCondition {
  field: string;
  equals?: unknown;
  notEquals?: unknown;
  isEmpty?: true;
  notEmpty?: true;
  gt?: number;
  gte?: number;
  lt?: number;
  lte?: number;
  in?: unknown[];
  matches?: string;
  contains?: unknown;
  notContains?: unknown;
}

// ─── Logical combiners ──────────────────────────────────────
export interface AllOfCondition {
  allOf: Condition[];
}

export interface AnyOfCondition {
  anyOf: Condition[];
}

export interface NotCondition {
  not: Condition;
}

// ─── Specialty conditions ───────────────────────────────────
export interface OptionPropCondition {
  optionProp: string;
  prop: string;
  equals?: unknown;
  notEquals?: unknown;
}

export interface MetaCondition {
  meta: string;
  equals?: unknown;
  notEquals?: unknown;
  /** Check if meta value is one of the listed values */
  in?: unknown[];
}

export interface CustomCondition {
  custom: string;
}

/**
 * Detects whether a field's value changed between the previous and current snapshots.
 * Requires `previousValues` to be set on the ConditionContext (provided by arrayEffectProcessor).
 *
 * Usage:
 *   { "field": "offeringPeriodStartDate", "changed": true }
 *   { "anyOf": [
 *     { "field": "offeringPeriodStartDate", "changed": true },
 *     { "field": "offeringPeriodEndDate", "changed": true }
 *   ]}
 */
export interface ChangedCondition {
  field: string;
  changed: true;
}

// ─── Value-level conditions (operate on current watched value) ─
export interface ValueEqualsCondition {
  equals: unknown;
}

export interface ValueNotEqualsCondition {
  notEquals: unknown;
}

export interface ValueIsEmptyCondition {
  isEmpty: true;
}

export interface ValueNotEmptyCondition {
  notEmpty: true;
}

export interface ValueGtCondition {
  gt: number;
}

export interface ValueGteCondition {
  gte: number;
}

export interface ValueLtCondition {
  lt: number;
}

export interface ValueLteCondition {
  lte: number;
}

export interface ValueInCondition {
  in: unknown[];
}

export interface ValueMatchesCondition {
  matches: string;
}

// ─── Known condition keys (for type guards) ─────────────────
export const CONDITION_KEYS = [
  'field', 'allOf', 'anyOf', 'not', 'optionProp', 'meta', 'custom', 'changed',
  'equals', 'notEquals', 'isEmpty', 'notEmpty',
  'gt', 'gte', 'lt', 'lte', 'in', 'matches',
  'contains', 'notContains',
] as const;
